/**
 * Everything derivable from a `MapDocument` that does not depend on the zoom.
 *
 * Building a `Scene` is the expensive step — label measurement, the O(n^2)
 * label thresholds, the O(n^2) mean pairwise distance — and it runs once per
 * data set (or per change of `scale`, which resizes every label).  Panning and
 * zooming then touch none of it.
 *
 * Typed arrays throughout: at the 2000-item cap the per-frame work is a linear
 * scan over these, and an array of objects would cost a pointer chase per item
 * for no gain in clarity.
 */

import type { MapDocument, MapItem, MapViewOptions, RGB } from '../types';
import type { Bounds } from '../geometry/viewport';
import { computeLabelThresholds } from './labels';
import {
  CIRCLE_AVERAGE_DIAMETER,
  CIRCLE_MIN_DIAMETER,
  DEFAULT_FONT_FAMILY,
  DENSITY_EXTENT_PADDING,
  KERNEL_WIDTH,
  LABEL_FONT_SIZE_SCALING,
  LABEL_MAX_LENGTH,
  LABEL_MIN_FONT_SIZE,
  LABEL_SIZE_VARIATION,
  LINE_AVERAGE_WIDTH,
  LINE_MIN_WIDTH,
  LINE_SIZE_VARIATION,
  LINE_STRENGTH_MEAN_TOP_N,
  MIN_RELATIVE_CLUSTER_WEIGHT,
  SUBLABEL_FONT_SIZE,
  SUBLABEL_MAX_REL_WIDTH,
  SUBLABEL_MIN_LENGTH,
} from './constants';
import { clusterColor, DEFAULT_CLUSTER_COLORS } from '../color/schemes';

/** Above this many items the mean pairwise distance is estimated from a sample. */
const EXACT_PAIRWISE_LIMIT = 6000;

export interface SceneOptions {
  scale: number;
  labelSizeVariation: number;
  maxLabelLength: number;
  fontFamily: string;
  kernelWidth: number;
  minRelativeClusterWeight: number;
  clusterBase: 0 | 1 | 'auto';
  clusterPalette: readonly RGB[];
}

export function resolveSceneOptions(options: MapViewOptions | undefined): SceneOptions {
  return {
    scale: options?.scale ?? 1,
    labelSizeVariation: options?.labelSizeVariation ?? LABEL_SIZE_VARIATION,
    maxLabelLength: options?.maxLabelLength ?? LABEL_MAX_LENGTH,
    fontFamily: options?.fontFamily ?? DEFAULT_FONT_FAMILY,
    kernelWidth: options?.kernelWidth ?? KERNEL_WIDTH,
    minRelativeClusterWeight: options?.minRelativeClusterWeight ?? MIN_RELATIVE_CLUSTER_WEIGHT,
    clusterBase: options?.clusterBase ?? 'auto',
    clusterPalette: options?.clusterPalette ?? DEFAULT_CLUSTER_COLORS,
  };
}

export interface Scene {
  readonly doc: MapDocument;
  readonly n: number;

  /** Layout coordinates. */
  readonly x: Float64Array;
  readonly y: Float64Array;

  /** `weight / mean(weight)`, VOSviewer's `Item.normalizedWeight`. */
  readonly normWeight: Float64Array;

  /** Circle diameter in CSS pixels, already floored the way Java's `(int)` cast does. */
  readonly diameter: Float64Array;

  /** Label font size in CSS pixels. */
  readonly fontSize: Float64Array;

  /** Label text after truncation to `maxLabelLength`. */
  readonly labelText: string[];

  /** Sublabel after truncation, or `''`. */
  readonly sublabelText: string[];

  /** Label box in CSS pixels at zoom 1 — what the threshold rule compares. */
  readonly frameW: Float64Array;
  readonly frameH: Float64Array;
  readonly mainW: Float64Array;
  readonly mainH: Float64Array;
  readonly subW: Float64Array;
  readonly subH: Float64Array;
  readonly mainAscent: Float64Array;
  readonly subAscent: Float64Array;

  /** Smallest viewport scale at which the label is drawn at full opacity. */
  readonly labelThreshold: Float64Array;

  /** Item indices sorted by descending weight; the draw and hit-test order. */
  readonly order: Int32Array;

  /** Zero-based cluster index, or -1 for "no cluster". */
  readonly cluster: Int32Array;

  /** Colour per item from its cluster. */
  readonly clusterRgb: Uint8Array;

  /** Zero-based indices of the clusters big enough for the cluster density view. */
  readonly largeClusters: number[];

  /** True when the item belongs to a large cluster. */
  readonly inLargeCluster: Uint8Array;

  readonly bounds: Bounds;

  /** Square, padded world box the density grid is sampled over. */
  readonly densityExtent: Bounds;

  /** Mean distance over all item pairs, VOSviewer's `Layout.getAverageDistance`. */
  readonly meanPairwiseDistance: number;

  /** `kernelWidth * meanPairwiseDistance` — the *h* in the density kernel. */
  readonly bandwidth: number;

  /** Link endpoints and strengths, sorted by descending strength. */
  readonly linkSource: Int32Array;
  readonly linkTarget: Int32Array;
  readonly linkStrength: Float64Array;

  /** Line width in CSS pixels, from the strength rescaled by the top-1000 mean. */
  readonly linkWidth: Float64Array;

  /** Every score field present on at least one item, in first-seen order. */
  readonly scoreFields: string[];

  readonly options: SceneOptions;
}

/** Java's `(int)` cast on a non-negative double. */
function truncate(v: number): number {
  return Math.floor(v);
}

function measureFont(size: number, family: string): string {
  return `${size}px ${family}`;
}

interface TextSize {
  width: number;
  height: number;
  ascent: number;
}

function measure(ctx: CanvasRenderingContext2D, text: string, fontSize: number): TextSize {
  const m = ctx.measureText(text);
  // fontBoundingBox* is what Java's FontMetrics.getAscent/getDescent report:
  // the *font's* extents, not this string's ink. Using actualBoundingBox would
  // make a label of "eee" shorter than one of "Ely", and the label boxes would
  // stop being comparable between items. Older engines omit it.
  const ascent = m.fontBoundingBoxAscent ?? m.actualBoundingBoxAscent ?? fontSize * 0.8;
  const descent = m.fontBoundingBoxDescent ?? m.actualBoundingBoxDescent ?? fontSize * 0.2;
  return { width: m.width, height: ascent + descent, ascent };
}

/**
 * Mean Euclidean distance over all pairs.
 *
 * Exact below `EXACT_PAIRWISE_LIMIT` items, which covers the 2000-item public
 * cap; above it, a deterministic every-k-th-item sample keeps the cost bounded.
 * The kernel width is a smoothing parameter, so a 1% error in it is invisible.
 */
export function meanPairwiseDistance(x: Float64Array, y: Float64Array): number {
  const n = x.length;
  if (n < 2) return 0;
  const stride = n <= EXACT_PAIRWISE_LIMIT ? 1 : Math.ceil(n / EXACT_PAIRWISE_LIMIT);
  let total = 0;
  let pairs = 0;
  for (let i = 0; i < n; i += stride) {
    const xi = x[i] as number;
    const yi = y[i] as number;
    for (let j = 0; j < i; j += stride) {
      const dx = xi - (x[j] as number);
      const dy = yi - (y[j] as number);
      total += Math.sqrt(dx * dx + dy * dy);
      pairs += 1;
    }
  }
  return pairs > 0 ? total / pairs : 0;
}

/**
 * The square, padded world box the density grid covers.
 *
 * DELIBERATE DEVIATION, shared with the Python side: VOSviewer pads the item
 * bounding box by 10% and then stretches it to the aspect ratio of the map
 * panel, so its 250x250 grid has non-square cells whose shape depends on the
 * window size — resize the window and the density field changes.  We square
 * the box, so the field is a property of the data alone.
 */
export function densityExtent(bounds: Bounds, border = DENSITY_EXTENT_PADDING): Bounds {
  const [xMin, yMin, xMax, yMax] = bounds;
  let width = xMax - xMin;
  let height = yMax - yMin;
  if (width <= 0 && height <= 0) {
    width = 1;
    height = 1;
  } else if (width <= 0) {
    width = height;
  } else if (height <= 0) {
    height = width;
  }
  const cx = (xMin + xMax) / 2;
  const cy = (yMin + yMax) / 2;
  width *= 1 + 2 * border;
  height *= 1 + 2 * border;
  const side = Math.max(width, height);
  return [cx - side / 2, cy - side / 2, cx + side / 2, cy + side / 2];
}

/**
 * Decide whether `MapItem.cluster` is 0-based or 1-based.
 *
 * VOSviewer map files are 1-based with 0 meaning "no cluster", and that is what
 * journalmap writes.  But a caller holding an in-memory clustering is just as
 * likely to hand us 0-based labels, and silently painting cluster 0 grey would
 * be a bug nobody spots.  Rule: 0-based if some item carries 0 and none carries
 * a negative; 1-based otherwise.  `MapViewOptions.clusterBase` overrides it.
 */
function detectClusterBase(items: readonly MapItem[]): 0 | 1 {
  let sawZero = false;
  for (const item of items) {
    const c = item.cluster;
    if (!Number.isFinite(c)) continue;
    if (c < 0) return 1;
    if (c === 0) sawZero = true;
  }
  return sawZero ? 0 : 1;
}

export function buildScene(
  doc: MapDocument,
  ctx: CanvasRenderingContext2D,
  options: SceneOptions,
): Scene {
  const items = doc.items;
  const n = items.length;
  const { scale, labelSizeVariation, maxLabelLength, fontFamily } = options;

  const x = new Float64Array(n);
  const y = new Float64Array(n);
  const normWeight = new Float64Array(n);
  const diameter = new Float64Array(n);
  const fontSize = new Float64Array(n);
  const frameW = new Float64Array(n);
  const frameH = new Float64Array(n);
  const mainW = new Float64Array(n);
  const mainH = new Float64Array(n);
  const subW = new Float64Array(n);
  const subH = new Float64Array(n);
  const mainAscent = new Float64Array(n);
  const subAscent = new Float64Array(n);
  const labelText: string[] = new Array<string>(n);
  const sublabelText: string[] = new Array<string>(n);
  const cluster = new Int32Array(n);
  const clusterRgb = new Uint8Array(n * 3);

  let weightSum = 0;
  for (let i = 0; i < n; i += 1) {
    const w = (items[i] as MapItem).weight;
    weightSum += Number.isFinite(w) && w > 0 ? w : 0;
  }
  const meanWeight = n > 0 ? weightSum / n : 0;

  const base = options.clusterBase === 'auto' ? detectClusterBase(items) : options.clusterBase;

  let xMin = Infinity;
  let yMin = Infinity;
  let xMax = -Infinity;
  let yMax = -Infinity;

  const scoreFields: string[] = [];
  const seenFields = new Set<string>();

  for (let i = 0; i < n; i += 1) {
    const item = items[i] as MapItem;
    const px = Number.isFinite(item.x) ? item.x : 0;
    const py = Number.isFinite(item.y) ? item.y : 0;
    x[i] = px;
    y[i] = py;
    if (px < xMin) xMin = px;
    if (px > xMax) xMax = px;
    if (py < yMin) yMin = py;
    if (py > yMax) yMax = py;

    const rawWeight = Number.isFinite(item.weight) && item.weight > 0 ? item.weight : 0;
    const w = meanWeight > 0 ? rawWeight / meanWeight : 1;
    normWeight[i] = w;

    const sized = Math.pow(w, labelSizeVariation);
    diameter[i] = truncate(scale * Math.max(CIRCLE_AVERAGE_DIAMETER * sized, CIRCLE_MIN_DIAMETER));
    fontSize[i] = truncate(scale * (LABEL_MIN_FONT_SIZE + LABEL_FONT_SIZE_SCALING * sized));

    const c = Number.isFinite(item.cluster) ? Math.round(item.cluster) - base : -1;
    cluster[i] = c >= 0 ? c : -1;

    if (item.scores) {
      for (const field of Object.keys(item.scores)) {
        if (!seenFields.has(field)) {
          seenFields.add(field);
          scoreFields.push(field);
        }
      }
    }
  }

  if (!Number.isFinite(xMin)) {
    xMin = -1;
    xMax = 1;
    yMin = -1;
    yMax = 1;
  }

  // Cluster colours, and which clusters are big enough for the density view.
  let maxCluster = -1;
  for (let i = 0; i < n; i += 1) {
    const c = cluster[i] as number;
    if (c > maxCluster) maxCluster = c;
  }
  const nClusters = maxCluster + 1;
  const clusterWeight = new Float64Array(Math.max(1, nClusters));
  let clusteredWeightTotal = 0;
  for (let i = 0; i < n; i += 1) {
    const c = cluster[i] as number;
    const rgb = clusterColor(c, options.clusterPalette);
    clusterRgb[i * 3] = rgb[0];
    clusterRgb[i * 3 + 1] = rgb[1];
    clusterRgb[i * 3 + 2] = rgb[2];
    if (c >= 0) {
      clusterWeight[c] = (clusterWeight[c] as number) + (normWeight[i] as number);
    }
    clusteredWeightTotal += normWeight[i] as number;
  }
  // VOSviewer's denominator is the total weight of *all* items, clustered or
  // not, so adding unclustered noise to a map really does shrink every
  // cluster's share. Reproduced.
  const largeClusters: number[] = [];
  for (let c = 0; c < nClusters; c += 1) {
    const share = clusteredWeightTotal > 0 ? (clusterWeight[c] as number) / clusteredWeightTotal : 0;
    if (share >= options.minRelativeClusterWeight) largeClusters.push(c);
  }
  const isLarge = new Uint8Array(Math.max(1, nClusters));
  for (const c of largeClusters) isLarge[c] = 1;
  const inLargeCluster = new Uint8Array(n);
  for (let i = 0; i < n; i += 1) {
    const c = cluster[i] as number;
    inLargeCluster[i] = c >= 0 && isLarge[c] === 1 ? 1 : 0;
  }

  // Label metrics. Two passes over the text per item; ~4000 measureText calls
  // at the cap, a few milliseconds, and only when the data or `scale` changes.
  const subSize = truncate(scale * SUBLABEL_FONT_SIZE);
  for (let i = 0; i < n; i += 1) {
    const item = items[i] as MapItem;
    const size = Math.max(1, fontSize[i] as number);
    const text = (item.label ?? '').slice(0, maxLabelLength);
    labelText[i] = text;
    ctx.font = measureFont(size, fontFamily);
    const main = measure(ctx, text, size);
    mainW[i] = main.width;
    mainH[i] = main.height;
    mainAscent[i] = main.ascent;

    const rawSub = item.sublabel ?? '';
    if (rawSub === '') {
      sublabelText[i] = '';
      subW[i] = 0;
      subH[i] = 0;
      subAscent[i] = 0;
      frameW[i] = main.width;
      frameH[i] = main.height;
      continue;
    }
    ctx.font = measureFont(Math.max(1, subSize), fontFamily);
    let sub = rawSub.slice(0, maxLabelLength);
    let subMetrics = measure(ctx, sub, subSize);
    const minLength = Math.min(SUBLABEL_MIN_LENGTH, maxLabelLength);
    // VOSviewer shortens a sublabel until it is no wider than 1.3x the label,
    // but never below 15 characters — so a long sublabel under a short label
    // stays readable rather than being cut to nothing.
    while (sub.length > minLength && subMetrics.width > SUBLABEL_MAX_REL_WIDTH * main.width) {
      sub = sub.slice(0, sub.length - 1);
      subMetrics = measure(ctx, sub, subSize);
    }
    sublabelText[i] = sub;
    subW[i] = subMetrics.width;
    subH[i] = subMetrics.height;
    subAscent[i] = subMetrics.ascent;
    frameW[i] = Math.max(main.width, subMetrics.width);
    frameH[i] = main.height + subMetrics.height;
  }

  // Descending weight. Ties broken by index so the order is stable across
  // rebuilds — an unstable order would make label thresholds jitter.
  const order = new Int32Array(n);
  for (let i = 0; i < n; i += 1) order[i] = i;
  const orderArray = Array.from(order);
  orderArray.sort((a, b) => (normWeight[b] as number) - (normWeight[a] as number) || a - b);
  order.set(orderArray);

  const labelThreshold = computeLabelThresholds(order, x, y, frameW, frameH);

  const bounds: Bounds = [xMin, yMin, xMax, yMax];
  const extent = densityExtent(bounds);
  const mpd = meanPairwiseDistance(x, y);
  // A one-item map, or every item stacked on one point, gives h = 0 and then
  // NaN everywhere. Fall back to the extent so such a map still draws.
  const bandwidth =
    mpd > 0
      ? options.kernelWidth * mpd
      : options.kernelWidth * Math.max(extent[2] - extent[0], extent[3] - extent[1]);

  const links = doc.links;
  const linkOrder = links
    .map((_, i) => i)
    .filter((i) => {
      const l = links[i];
      if (!l) return false;
      return (
        l.source >= 0 &&
        l.source < n &&
        l.target >= 0 &&
        l.target < n &&
        l.source !== l.target &&
        Number.isFinite(l.strength)
      );
    });
  linkOrder.sort((a, b) => {
    const sa = (links[a] as { strength: number }).strength;
    const sb = (links[b] as { strength: number }).strength;
    return sb - sa || a - b;
  });
  const m = linkOrder.length;
  const linkSource = new Int32Array(m);
  const linkTarget = new Int32Array(m);
  const linkStrength = new Float64Array(m);
  for (let k = 0; k < m; k += 1) {
    const l = links[linkOrder[k] as number];
    if (!l) continue;
    linkSource[k] = l.source;
    linkTarget[k] = l.target;
    linkStrength[k] = l.strength;
  }

  // `MapData` rescales link strengths by the mean of the 1000 strongest, not by
  // the mean of all of them: a map with a long tail of weak links would
  // otherwise draw every visible link at the same hairline width.
  const topN = Math.min(m, LINE_STRENGTH_MEAN_TOP_N);
  let strengthSum = 0;
  for (let k = 0; k < topN; k += 1) strengthSum += linkStrength[k] as number;
  const meanStrength = topN > 0 ? strengthSum / topN : 0;
  const linkWidth = new Float64Array(m);
  for (let k = 0; k < m; k += 1) {
    const rescaled = meanStrength > 0 ? (linkStrength[k] as number) / meanStrength : 1;
    linkWidth[k] =
      scale *
      Math.max(LINE_AVERAGE_WIDTH * Math.pow(Math.max(rescaled, 0), LINE_SIZE_VARIATION), LINE_MIN_WIDTH);
  }

  return {
    doc,
    n,
    x,
    y,
    normWeight,
    diameter,
    fontSize,
    labelText,
    sublabelText,
    frameW,
    frameH,
    mainW,
    mainH,
    subW,
    subH,
    mainAscent,
    subAscent,
    labelThreshold,
    order,
    cluster,
    clusterRgb,
    largeClusters,
    inLargeCluster,
    bounds,
    densityExtent: extent,
    meanPairwiseDistance: mpd,
    bandwidth,
    linkSource,
    linkTarget,
    linkStrength,
    linkWidth,
    scoreFields,
    options,
  };
}
